import React, { useMemo, useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { Popover } from "antd";
import classNames from "classnames";
import type { ISegment, StopType } from "@typing/wsui/getMessages";
import styles from "./index.module.less";
import { useReferenceStore } from "@store/referenceStore";
import { useHeguiNavigate } from "@hooks/useHeguiNavigate";

// 引用图标
let iconExpandDown = "https://muzhi-public-pic.cdn.bcebos.com/100115338615";
let iconThinking = "https://muzhi-public-pic.cdn.bcebos.com/100115350876";
let iconCheckGreen = "https://muzhi-public-pic.cdn.bcebos.com/100115337891";
let iconCheck = "https://muzhi-public-pic.cdn.bcebos.com/100115350860";
let iconStop = "https://muzhi-public-pic.cdn.bcebos.com/100115351369";
let iconDocument = "https://muzhi-public-pic.cdn.bcebos.com/100115350845";
let iconLink = "https://muzhi-public-pic.cdn.bcebos.com/100115350874";

/**
 * ThinkProcessV2 JSON 数据类型定义
 * 基于 mock/response/wsui/oe_advisor_getmessages.js 中的 content 字段
 */

// 文献/文档信息
interface IThinkDoc {
    title: string;
    source?: string;
    url: string;
    /** BOS 存储 key */
    bos_key?: string;
    /** 合规加密字符串 */
    hegui_info?: string;
    /** 是否需要请求合规校验接口 */
    need_hegui?: boolean;
}

// 引用文献详细信息
export interface IThinkReference {
    index: number;
    id: string;
    title: string;
    desc_list: string[];
    source: string; // 论文、指南、期刊、药品、书籍等
    url: string;
    content_list: string[];
    tool_tip: {
        fragment_id: string;
        tools: Array<{
            type: "like" | "unlike";
            status: number; // 0-未操作, 1-已操作
            popupConfig?: any;
        }>;
    };
    /** BOS 存储 key */
    bos_key?: string;
    /** 合规加密字符串 */
    hegui_info?: string;
    /** 是否需要请求合规校验接口 */
    need_hegui?: boolean;
}

// 思考步骤
interface IThinkStep {
    type: "parse" | "search" | "arrange"; // parse-解析理解, search-检索, arrange-整理
    status: "done" | "running" | "stop"; // 完成状态: done-完成, running-进行中, stop-已停止
    desc: string; // 步骤描述
    extra?: string; // 补充说明（parse类型，keywords 为空时展示）
    keywords?: string[]; // 关键词列表（parse类型）
    docs?: IThinkDoc[]; // 文档列表（search类型）
}

// ThinkProcessV2 完整数据结构
interface IThinkProcessV2Data {
    title: string; // 标题，如"文献检索中..."
    sub_title: string; // 副标题，如"深度检索：基于权威专业信息的综合研究"
    think_text: string; // 思考状态文本，如"思考中"
    steps: IThinkStep[]; // 步骤列表
    references: IThinkReference[]; // 引用文献列表
}

interface IThinkingProcessV2Props {
    segment: ISegment; // segment 数据
    sessionId: string; // 会话 ID
    messageId: string; // 消息 ID
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    stopType?: StopType; // 停止类型（用于视图层处理最后一个 step 的状态）
}

/**
 * 自动收起 Hook
 * 当 isFinished 从 false 变为 true 时，自动调用 onCollapse 收起
 *
 * @param isFinished 是否完成
 * @param isExpanded 当前是否展开
 * @param onCollapse 收起回调
 */
const useAutoCollapseOnFinish = (isFinished: boolean, isExpanded: boolean, onCollapse?: () => void) => {
    const prevIsFinishedRef = useRef(isFinished);
    const isExpandedRef = useRef(isExpanded);
    const onCollapseRef = useRef(onCollapse);

    // 保持 refs 是最新的值
    isExpandedRef.current = isExpanded;
    onCollapseRef.current = onCollapse;

    useEffect(() => {
        // 检测 isFinished 从 false 变为 true 的时刻
        if (isFinished && !prevIsFinishedRef.current && isExpandedRef.current) {
            onCollapseRef.current?.();
        }
        prevIsFinishedRef.current = isFinished;
    }, [isFinished]); // 只依赖 isFinished，避免其他依赖变化干扰检测
};

/**
 * 关键词标签组件Props
 */
interface IKeywordTagsProps {
    keywords: string[];
    stepIndex: number;
}

/**
 * 关键词标签组件
 * 规则1: 普通标签最多显示3行，超出的用 +x 替代
 * 规则2: 在规则1下，最多展示4个标签
 */
const MAX_VISIBLE_TAGS = 4; // 最多显示4个标签

const KeywordTags: React.FC<IKeywordTagsProps> = ({ keywords, stepIndex }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState<number>(Math.min(keywords.length, MAX_VISIBLE_TAGS));
    const [expanded, setExpanded] = useState<boolean>(false);
    const prevKeywordsLengthRef = useRef<number>(keywords.length);

    useLayoutEffect(() => {
        if (!containerRef.current) return;

        // 如果 keywords 长度变化了，重置 visibleCount
        if (prevKeywordsLengthRef.current !== keywords.length) {
            prevKeywordsLengthRef.current = keywords.length;
            setVisibleCount(Math.min(keywords.length, MAX_VISIBLE_TAGS));
            return;
        }

        const container = containerRef.current;
        const children = Array.from(container.children) as HTMLElement[];

        if (children.length === 0) return;

        // 获取普通标签（排除最后的 +x）
        const hasPlus = visibleCount < keywords.length;
        const tags = hasPlus ? children.slice(0, -1) : children;

        if (tags.length === 0) return;

        // 通过 offsetTop 变化来检测换行
        let currentRow = 1;
        let prevTop = tags[0].offsetTop;

        for (let i = 0; i < tags.length; i++) {
            if (tags[i].offsetTop > prevTop) {
                currentRow++;
                prevTop = tags[i].offsetTop;
            }

            // 如果这个标签在第4行，减少显示数量（同时受 MAX_VISIBLE_TAGS 限制）
            if (currentRow >= 4) {
                const newCount = Math.min(i, MAX_VISIBLE_TAGS);
                if (visibleCount !== newCount) {
                    setVisibleCount(newCount);
                }
                return;
            }
        }

        // 即使3行内能显示完，也要限制最多4个
        const maxAllowed = Math.min(tags.length, MAX_VISIBLE_TAGS);
        if (visibleCount > maxAllowed) {
            setVisibleCount(maxAllowed);
        }
    }, [visibleCount, keywords.length]);

    // 最终显示数量受 MAX_VISIBLE_TAGS 限制
    const finalVisibleCount = Math.min(visibleCount, MAX_VISIBLE_TAGS);
    const displayKeywords = expanded ? keywords : keywords.slice(0, finalVisibleCount);
    const remainingCount = keywords.length - finalVisibleCount;

    const handleExpandClick = () => {
        setExpanded(true);
    };

    return (
        <div ref={containerRef} className={styles.tagsList}>
            {displayKeywords.map((keyword, tagIndex) => (
                <div key={`${stepIndex}-${tagIndex}`} className={styles.tag}>
                    {keyword}
                </div>
            ))}
            {!expanded && remainingCount > 0 && (
                <div className={styles.countTag} onClick={handleExpandClick}>
                    +{remainingCount}
                </div>
            )}
        </div>
    );
};

/**
 * 单个文献项组件 - 带截断检测
 */
interface IReferenceItemInnerProps {
    doc: IThinkDoc;
    docIndex: number;
    /** 点击跳转回调，用于合规校验 */
    onNavigate?: (doc: IThinkDoc) => void;
}

const ReferenceItemInner: React.FC<IReferenceItemInnerProps> = ({ doc, docIndex, onNavigate }) => {
    const titleRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipWidth, setTooltipWidth] = useState<number>(0);

    // 鼠标进入时检测是否截断，并获取宽度
    const handleMouseEnter = useCallback(() => {
        if (titleRef.current) {
            const isTruncated = titleRef.current.scrollWidth > titleRef.current.clientWidth;
            if (isTruncated) {
                setTooltipWidth(titleRef.current.clientWidth);
                setShowTooltip(true);
            }
        }
    }, []);

    // 鼠标离开时关闭 Tooltip
    const handleMouseLeave = useCallback(() => {
        setShowTooltip(false);
    }, []);

    // 点击处理 - 支持合规校验
    const handleClick = useCallback(() => {
        if (onNavigate) {
            onNavigate(doc);
        } else if (doc.url) {
            window.open(doc.url, "_blank");
        }
    }, [doc, onNavigate]);

    return (
        <div
            key={docIndex}
            className={styles.referenceItem}
            onClick={handleClick}
            style={{ cursor: doc.url ? "pointer" : "default" }}
        >
            <div className={styles.referenceIcon}>
                <img src={iconLink} alt="文档" />
            </div>
            <div className={styles.referenceType}>{doc.source || "文献"}</div>
            <Popover
                content={<div style={{ wordBreak: "break-word" }}>{doc.title}</div>}
                open={showTooltip}
                overlayStyle={{
                    maxWidth: tooltipWidth > 0 ? tooltipWidth : "none",
                }}
                arrow={false}
                destroyOnHidden
                autoAdjustOverflow={false}
            >
                <div
                    ref={titleRef}
                    className={styles.referenceTitle}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {doc.title}
                </div>
            </Popover>
        </div>
    );
};

/**
 * 文献列表组件Props
 */
interface IReferencesListProps {
    docs: IThinkDoc[];
    isExpanded: boolean;
    onToggle: () => void;
    /** 点击跳转回调，用于合规校验 */
    onNavigate?: (doc: IThinkDoc) => void;
}

/**
 * 文献列表组件
 * 默认最多显示3条，超过3条时显示展开按钮
 */
const ReferencesList: React.FC<IReferencesListProps> = ({ docs, isExpanded, onToggle, onNavigate }) => {
    const MAX_DISPLAY = 3;
    const shouldShowExpandButton = docs.length > MAX_DISPLAY;
    const displayDocs = isExpanded ? docs : docs.slice(0, MAX_DISPLAY);

    return (
        <div className={styles.referencesList}>
            {displayDocs.map((doc, docIndex) => (
                <ReferenceItemInner key={docIndex} doc={doc} docIndex={docIndex} onNavigate={onNavigate} />
            ))}

            {/* 展开全部文献按钮 - 只在超过3条时显示 */}
            {shouldShowExpandButton && (
                <div className={styles.expandAllWrapper}>
                    <div className={styles.expandAllButton} onClick={onToggle}>
                        <span>{isExpanded ? "收起文献" : `展开全部${docs.length}篇文献`}</span>
                        <img
                            src={iconExpandDown}
                            alt={isExpanded ? "收起" : "展开"}
                            className={styles.expandIcon}
                            style={{
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * 终止状态场景 key 类型
 */
type TerminationSceneKey = "title" | "thinkText" | "thinkingIcon" | "statusText";

/**
 * 场景状态对象
 */
interface ISceneState {
    class: string;
    text: string;
    dom: React.ReactNode;
}

/**
 * 创建终止状态工具函数
 * 统一处理 stopType 相关的状态、样式、文案和 DOM
 *
 * @param isFinished segment 是否完成
 * @param stopType 停止类型
 * @param defaultTexts 默认文案配置
 * @returns getStatus 函数
 *
 * @example
 * const getStatus = createTerminationState(segment.is_finished, stopType, { title, thinkText: think_text });
 * getStatus('title').text         // "停止检索" 或 原标题
 * getStatus('title').dom          // 加载省略号 DOM 或 null
 * getStatus('thinkingIcon').class // 图标样式类名
 * getStatus('statusText').class   // 状态文字样式类名
 */
const createTerminationState = (
    isFinished: boolean,
    stopType: StopType | undefined,
    defaultTexts: { title: string; thinkText: string }
) => {
    // 是否已终止（segment 完成 或 有 stopType）
    const isTerminated = isFinished || !!stopType;
    // 是否被用户停止
    const isStopped = !!stopType;

    // 加载省略号 DOM
    const loadingDotsDom = !isTerminated ? (
        <span className={styles.loadingDots}>
            <span className={styles.dot}>.</span>
            <span className={styles.dot}>.</span>
            <span className={styles.dot}>.</span>
        </span>
    ) : null;

    // 场景配置映射表
    const sceneMap: Record<TerminationSceneKey, ISceneState> = {
        title: {
            class: "",
            text: isStopped ? "停止检索" : defaultTexts.title,
            dom: loadingDotsDom,
        },
        thinkText: {
            class: "",
            text: isStopped ? "停止思考" : defaultTexts.thinkText || "思考完成",
            dom: null,
        },
        thinkingIcon: {
            class: isTerminated ? styles.finished : "",
            text: "",
            dom: null,
        },
        statusText: {
            class: isTerminated ? styles.finishedText : styles.shining,
            text: "",
            dom: null,
        },
    };

    /**
     * 获取指定场景的状态
     */
    const getStatus = (key: TerminationSceneKey): ISceneState => sceneMap[key];

    // 暴露 isStopped 供 renderStepIcon 使用
    getStatus.isStopped = isStopped;

    return getStatus;
};

/**
 * 思考过程V2组件 - 权威文献检索样式
 * 展示AI的思考过程，包括文献检索和分析
 */
const ThinkingProcessV2: React.FC<IThinkingProcessV2Props> = ({
    segment,
    sessionId,
    messageId,
    isExpanded = false,
    onToggleExpand,
    stopType,
}) => {
    // 文献列表展开状态（每个步骤独立管理）
    const [expandedReferences, setExpandedReferences] = useState<Record<number, boolean>>({});
    // 获取 store 的方法和状态
    const {
        openDrawer,
        closeDrawer,
        visible,
        sessionId: drawerSessionId,
        messageId: drawerMessageId,
    } = useReferenceStore();
    // 合规校验跳转 hook
    const { navigate: heguiNavigate } = useHeguiNavigate();

    // 解析 segment.content JSON 数据
    const thinkData = useMemo<IThinkProcessV2Data | null>(() => {
        try {
            return JSON.parse(segment.content) as IThinkProcessV2Data;
        } catch (e) {
            console.error("Failed to parse thinkProcessV2 content:", e);
            return null;
        }
    }, [segment.content]);

    useAutoCollapseOnFinish(segment.is_finished, isExpanded, onToggleExpand);

    // 切换侧边栏：如果当前抽屉已打开且是同一消息，则关闭；否则打开
    // 若没有引用文献，点击不展开侧边列表
    const toggleDrawer = useCallback(() => {
        if (!thinkData?.references || thinkData.references.length === 0) {
            return;
        }
        if (visible && drawerSessionId === sessionId && drawerMessageId === messageId) {
            closeDrawer();
        } else {
            openDrawer(sessionId, messageId);
        }
    }, [
        visible,
        drawerSessionId,
        drawerMessageId,
        sessionId,
        messageId,
        closeDrawer,
        openDrawer,
        thinkData?.references,
    ]);

    // 如果解析失败，不渲染
    if (!thinkData) {
        return null;
    }

    const { title, sub_title, steps, think_text } = thinkData;
    const lastStep = steps?.[steps.length - 1];
    // 最后一个 step 无 extra、无 keywords、无 docs 时（内容卡边），隐藏其尾部竖线
    const shouldHideLastStepTailLine = Boolean(
        lastStep && !lastStep.extra?.trim() && !lastStep.keywords?.length && !lastStep.docs?.length
    );

    // 获取终止状态工具函数
    const getTerminationStatus = createTerminationState(segment.is_finished, stopType, {
        title,
        thinkText: think_text,
    });
    const statusThinkText = getTerminationStatus("thinkText").text;
    const shouldAppendThinkingDots = !segment.is_finished && !stopType && statusThinkText === "思考中";

    const renderStepIcon = (step: IThinkStep, isLast: boolean) => {
        const isThinking = !segment.is_finished && !stopType;
        // 停止思考时，所有 step 的圆点都展示停止态
        const stepIsStopped = step.status === "stop" || getTerminationStatus.isStopped;
        const isFinished = !stepIsStopped && (isThinking ? !isLast : step.status === "done");
        const isRunning = !stepIsStopped && (isThinking ? isLast : step.status === "running");

        // 根据状态选择图标
        const getIcon = () => {
            if (stepIsStopped) return iconStop;
            if (isFinished) return iconCheck;
            return iconCheckGreen; // running
        };

        // 根据状态选择 alt 文本
        const getAlt = () => {
            if (stepIsStopped) return "已停止";
            if (isFinished) return "完成";
            return "进行中";
        };

        return (
            <div className={styles.stepIconWrapper}>
                <img
                    src={getIcon()}
                    alt={getAlt()}
                    className={`${styles.stepIcon} ${isRunning ? styles.rotating : ""}`}
                />
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {/* 顶部渐变区域 */}

            {/* 主体内容区域 */}
            <div className={styles.body}>
                {/* 思考完成标题 - 可点击展开/收起 */}
                <div className={styles.statusHeader} onClick={onToggleExpand} style={{ cursor: "pointer" }}>
                    <div className={styles.statusIconWrapper}>
                        <img
                            src={iconThinking}
                            alt="思考"
                            className={`${styles.thinkingIcon} ${getTerminationStatus("thinkingIcon").class}`}
                        />
                    </div>
                    <div className={styles.statusText}>
                        <span className={getTerminationStatus("statusText").class}>
                            {statusThinkText}
                            {shouldAppendThinkingDots ? "..." : ""}
                        </span>
                        <img
                            src={iconExpandDown}
                            alt={isExpanded ? "收起" : "展开"}
                            className={styles.arrowDownIcon}
                            style={{
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.3s ease",
                            }}
                        />
                    </div>
                </div>

                {/* 思考步骤列表 - 根据isExpanded控制显示 */}
                {isExpanded && (
                    <div
                        className={classNames(styles.stepsList, {
                            [styles.stepsListWithoutLastTail]: shouldHideLastStepTailLine,
                        })}
                    >
                        {steps?.map((step, index) => {
                            const isLast = index === steps.length - 1;
                            const hasDesc = step.desc.trim().length > 0;
                            const extraText = step.extra?.trim();

                            return (
                                <div key={index} className={styles.stepItem}>
                                    <div className={styles.stepIconColumn}>{renderStepIcon(step, isLast)}</div>

                                    <div className={styles.stepContent}>
                                        {hasDesc && <div className={styles.stepTitle}>{step.desc}</div>}

                                        {/* 标签列表 - parse类型显示keywords，最多显示三行 */}
                                        {step.keywords && step.keywords.length > 0 && (
                                            <KeywordTags keywords={step.keywords} stepIndex={index} />
                                        )}

                                        {/* extra 补充说明 - keywords 为空时展示 */}
                                        {(!step.keywords || step.keywords.length === 0) && extraText && (
                                            <div
                                                className={classNames(styles.stepExtra, {
                                                    [styles.stepExtraPrimary]: !hasDesc,
                                                })}
                                            >
                                                {extraText}
                                            </div>
                                        )}

                                        {/* 文献引用列表 - search类型显示docs */}
                                        {step.docs && step.docs.length > 0 && (
                                            <ReferencesList
                                                docs={step.docs}
                                                isExpanded={expandedReferences[index] || false}
                                                onToggle={() => {
                                                    setExpandedReferences((prev) => ({
                                                        ...prev,
                                                        [index]: !prev[index],
                                                    }));
                                                }}
                                                onNavigate={heguiNavigate}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ThinkingProcessV2;
